/**
 * ImpersonationContext — Admin "View as" functionality.
 *
 * Allows admin users (ldap_groups includes 'rocky-admin') to view
 * another user's data in read-only mode. All data-fetching modules
 * should use `effectiveUid` instead of `uid` to respect this.
 *
 * The target survives a reload: it is kept in localStorage, base64-encoded (see
 * impersonationStorage for why, and for why that is not a security boundary). It used to be
 * in-memory only, so every refresh dropped the admin back to their own view mid-investigation.
 * The banner is what keeps a persisted view from being forgotten.
 *
 * Persisting it raises cases in-memory state never had:
 *
 *   - The browser is shared by whoever signs in on it, so the stored value records who set it
 *     and is only honoured for that person (see impersonationStorage). Without that, an admin
 *     who signed in after another would inherit their view.
 *   - The stored target is honoured only while the caller is still an admin, so losing the
 *     group ends the view even if a value was left behind.
 *   - Other tabs share the value. Exiting in one tab exits in all of them (the `storage` event),
 *     so a tab left open cannot keep showing someone else's data after the admin thinks they
 *     have stopped.
 *
 * `uid` is read on this provider's first render. That is safe because AuthContextProvider does
 * not render its children until it has a token, so the signed-in user is already known here.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useAuth } from './AuthContext';
import {
  IMPERSONATION_STORAGE_KEY,
  VIEW_AS_ADMIN_GROUP,
  isValidAlias,
  loadImpersonation,
  normaliseAlias,
  saveImpersonation,
} from './impersonationStorage';

// ── Types ───────────────────────────────────────────────────────────────────

interface ImpersonationState {
  /** Whether currently impersonating another user */
  isImpersonating: boolean;
  /** Target alias being viewed (null when not impersonating) */
  targetAlias: string | null;
  /** The effective UID for all data queries (targetAlias or self) */
  effectiveUid: string;
  /** Whether the current user has admin privileges */
  isAdmin: boolean;
  /** Enter impersonation mode */
  enterImpersonation: (alias: string) => void;
  /** Exit impersonation mode */
  exitImpersonation: () => void;
}

// ── Constants ───────────────────────────────────────────────────────────────

const ADMIN_LDAP_GROUP = VIEW_AS_ADMIN_GROUP;

// ── Context ─────────────────────────────────────────────────────────────────

const ImpersonationContext = createContext<ImpersonationState>({
  isImpersonating: false,
  targetAlias: null,
  effectiveUid: '',
  isAdmin: false,
  enterImpersonation: () => {},
  exitImpersonation: () => {},
});

export const useImpersonation = () => useContext(ImpersonationContext);

// ── Provider ────────────────────────────────────────────────────────────────

export function ImpersonationProvider({ children }: { children: ReactNode }) {
  const { uid, ldap_groups } = useAuth();
  // Lazy: storage is read once, on mount, not on every render.
  const [storedAlias, setStoredAlias] = useState<string | null>(() => loadImpersonation(uid));

  const isAdmin = useMemo(
    () => ldap_groups.includes(ADMIN_LDAP_GROUP),
    [ldap_groups],
  );

  // Honoured only while the caller is still an admin.
  const targetAlias = isAdmin ? storedAlias : null;
  const isImpersonating = targetAlias !== null && targetAlias !== uid;

  const effectiveUid = isImpersonating ? targetAlias! : uid;

  // Another tab entered, switched or exited — follow it.
  useEffect(() => {
    function onStorage(e: StorageEvent) {
      if (e.key === IMPERSONATION_STORAGE_KEY) setStoredAlias(loadImpersonation(uid));
    }
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [uid]);

  const enterImpersonation = useCallback((alias: string) => {
    if (!isAdmin) {
      console.warn('[Impersonation] Non-admin attempted impersonation');
      return;
    }
    const normalised = normaliseAlias(alias);
    /* Refused rather than stored: the Event Hub service rejects a malformed target with a 403
       on every request, which would look like the whole module had broken. */
    if (!isValidAlias(normalised)) return;
    saveImpersonation(uid, normalised);
    setStoredAlias(normalised);
  }, [isAdmin, uid]);

  const exitImpersonation = useCallback(() => {
    saveImpersonation(uid, null);
    setStoredAlias(null);
  }, [uid]);

  const value = useMemo(() => ({
    isImpersonating,
    targetAlias,
    effectiveUid,
    isAdmin,
    enterImpersonation,
    exitImpersonation,
  }), [isImpersonating, targetAlias, effectiveUid, isAdmin, enterImpersonation, exitImpersonation]);

  return (
    <ImpersonationContext.Provider value={value}>
      {children}
    </ImpersonationContext.Provider>
  );
}
